import { useState, ChangeEvent } from 'react'
import { Button, Form, Checkbox, Col, Row, message } from 'antd'
import Modal from 'antd/lib/modal/Modal'
import TextArea from 'antd/lib/input/TextArea'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'
import { useApolloClient } from '@apollo/client'
import { quoteRequestChangeMutation } from '@Builder/QuoteDetailPage/logic'
import { useDispatch } from 'react-redux'

import Text from '~/core/Text'
import { REQUEST_CHANGE_CHECKBOX_OPTIONS } from '../constants'
import Box from '~/core/Box'
import {
    getCoreQuoteDetailsData,
    getLegacyQuoteDetailsData,
} from '~/redux-modules/query-refactor/actions'
type RequestChangeModalPropType = {
    isVisible: boolean
    closeModal: () => void
    quoteId: string | undefined
    uploadRevision?: boolean
}

function RequestChangeModal(props: RequestChangeModalPropType) {
    const { isVisible, closeModal, quoteId, uploadRevision } = props
    const client = useApolloClient()
    const initialStateCheckedList = uploadRevision ? ['designChange'] : []
    const [requestChangeText, setRequestChangeText] = useState('')
    const [requestChangeCheckedList, setRequestChangeCheckedList] = useState<
        CheckboxValueType[]
    >(initialStateCheckedList)
    const onTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setRequestChangeText(event.target.value)
    }
    const onCheckBoxChange = (checkedValues: CheckboxValueType[]) => {
        setRequestChangeCheckedList(checkedValues)
    }
    const dispatch = useDispatch()
    const resetState = () => {
        setRequestChangeCheckedList(initialStateCheckedList)
        setRequestChangeText('')
    }
    const onSubmit = async () => {
        try {
            // eslint-disable-next-line no-unsafe-optional-chaining
            const response = await client?.mutate({
                mutation: quoteRequestChangeMutation,
                variables: {
                    submitQuoteChangeRequestId: quoteId,
                    changeRequestInput: {
                        changeDescription: requestChangeText,
                        changeTypes: {
                            configurationChange:
                                requestChangeCheckedList.includes(
                                    'configurationChange' as never,
                                ),
                            designChange: requestChangeCheckedList.includes(
                                'designChange' as never,
                            ),
                            otherChange: requestChangeCheckedList.includes(
                                'otherChange' as never,
                            ),
                            quantityChange: requestChangeCheckedList.includes(
                                'quantityChange' as never,
                            ),
                        },
                    },
                },
            })

            if (!response?.data.submitQuoteChangeRequest.isLocked) {
                message.success('You may now make changes to your quote.')
                window.localStorage.removeItem('modal-status')
                resetState()
                closeModal()
            }
            if (response?.data.submitQuoteChangeRequest.isLocked) {
                message.success('Change requested')
                window.localStorage.removeItem('modal-status')
                resetState()
                closeModal()
                //Todo A new modal with progress tracker needs to be displayed after this
            }

            dispatch(getCoreQuoteDetailsData(quoteId as string))
            dispatch(getLegacyQuoteDetailsData(quoteId as string))
        } catch (error) {
            message.error('Failed to send request')
        }
    }

    return (
        <Modal
            title="Request changes to your quote"
            visible={isVisible}
            footer={
                <>
                    <Button onClick={closeModal}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={onSubmit}
                        disabled={requestChangeCheckedList.length < 1}
                    >
                        Submit Change Request
                    </Button>
                </>
            }
            onCancel={() => closeModal()}
            width="600px"
        >
            <Text
                style={{
                    color: 'rgba(0, 0, 0, 0.65)',
                    fontWeight: 400,
                    fontSize: '14px',
                }}
            >
                Select the nature of the changes you’d like to make (select all
                that apply).
            </Text>
            <Form>
                <Box
                    borderBottom="1px solid rgba(0, 0, 0, 0.09)"
                    direction="col"
                    padding="0 0 16px 0"
                >
                    <Col>
                        <Checkbox.Group
                            onChange={checkedValues =>
                                onCheckBoxChange(checkedValues)
                            }
                            defaultValue={
                                uploadRevision ? ['designChange'] : []
                            }
                            value={requestChangeCheckedList}
                        >
                            {REQUEST_CHANGE_CHECKBOX_OPTIONS.map(option => (
                                <Row key={option.value}>
                                    <Checkbox value={option.value}>
                                        {option.label}
                                    </Checkbox>
                                </Row>
                            ))}
                        </Checkbox.Group>
                    </Col>
                </Box>
                <Box direction="col" margin="16px 0 0 0">
                    <Text
                        style={{
                            color: 'rgba(0, 0, 0, 0.65)',
                            fontWeight: 400,
                            fontSize: '14px',
                        }}
                    >
                        Describe your desired changes to this quote
                    </Text>
                    <Text
                        style={{
                            color: 'rgba(0, 0, 0, 0.45)',
                            fontWeight: 300,
                            fontSize: '14px',
                        }}
                    >
                        e.g. Updated quantities, lead time, etc.
                    </Text>
                    <TextArea
                        style={{
                            margin: '16px 0 0 0',
                            height: '60px',
                        }}
                        cols={150}
                        onChange={event => onTextAreaChange(event)}
                        value={requestChangeText}
                        maxLength={800}
                    />
                    <Text
                        style={{
                            fontWeight: 300,
                            fontSize: '11px',
                            color: 'rgba(0, 0, 0, 0.45)',
                            marginTop: '8px',
                        }}
                    >
                        300 Characters Max
                    </Text>
                </Box>
            </Form>
        </Modal>
    )
}

export default RequestChangeModal
